import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  RefreshCw,
  Search,
  ShoppingCart,
  Package,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Zap,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Layers,
  GitCompare,
  X,
  Sparkles,
} from 'lucide-react';
import { Button } from '../ui/index.js';
import { cn } from '../ui/cn.js';
import {
  fetchSalesByBrand,
  fetchSalesSyncStatus,
  triggerOrdersSync,
  type BrandSalesRow,
} from '../api.js';

function formatTnd(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return (
    value.toLocaleString('fr-FR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }) + ' TND'
  );
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return 'à l’instant';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `il y a ${diffMin} min`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `il y a ${diffHours} h`;
  const diffDays = Math.floor(diffHours / 24);
  return `il y a ${diffDays} j`;
}

type PeriodType = 'year' | 'quarter' | 'duration' | 'custom';
type CompareMode = 'yoy' | 'previous_period';
type TableFilter = 'all' | 'growing' | 'declining' | 'grouped';

const AVAILABLE_YEARS = [2026, 2025, 2024, 2023];

const QUARTER_RANGES: Record<number, { start: string; end: string; label: string }> = {
  1: { start: '01-01', end: '03-31', label: 'T1 (Janv. — Mars)' },
  2: { start: '04-01', end: '06-30', label: 'T2 (Avr. — Juin)' },
  3: { start: '07-01', end: '09-30', label: 'T3 (Juil. — Sept.)' },
  4: { start: '10-01', end: '12-31', label: 'T4 (Oct. — Déc.)' },
};

export function SalesByBrandPage(): React.ReactElement {
  const queryClient = useQueryClient();

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentQuarter = Math.floor(now.getMonth() / 3) + 1;
  const todayStr = now.toISOString().slice(0, 10);

  // Period state
  const [periodType, setPeriodType] = useState<PeriodType>('year');
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState<number>(currentQuarter);
  const [selectedDuration, setSelectedDuration] = useState<string>('90');
  const [customFrom, setCustomFrom] = useState<string>('2026-01-01');
  const [customTo, setCustomTo] = useState<string>(todayStr);

  // Comparison state
  const [compareEnabled, setCompareEnabled] = useState<boolean>(false);
  const [compareMode, setCompareMode] = useState<CompareMode>('yoy');

  // Filter & Brand Grouping state
  const [groupBrands, setGroupBrands] = useState<boolean>(true);
  const [sort, setSort] = useState<'qty' | 'revenue' | 'orders' | 'products'>('revenue');
  const [search, setSearch] = useState<string>('');
  const [tableFilter, setTableFilter] = useState<TableFilter>('all');
  const [includeProcessing, setIncludeProcessing] = useState<boolean>(true);
  const [expandedBrands, setExpandedBrands] = useState<Set<string>>(new Set());

  // Head-to-Head Brand Duel Modal
  const [duelOpen, setDuelOpen] = useState<boolean>(false);
  const [duelBrandA, setDuelBrandA] = useState<string>('');
  const [duelBrandB, setDuelBrandB] = useState<string>('');

  // Compute active (from, to)
  const { from, to, periodLabel } = useMemo(() => {
    if (periodType === 'year') {
      const isCurr = selectedYear === currentYear;
      return {
        from: `${selectedYear}-01-01`,
        to: isCurr ? todayStr : `${selectedYear}-12-31`,
        periodLabel: isCurr ? `Année en cours (${selectedYear})` : `Année ${selectedYear}`,
      };
    }
    if (periodType === 'quarter') {
      const q = QUARTER_RANGES[selectedQuarter] ?? QUARTER_RANGES[1]!;
      const isCurrQ = selectedYear === currentYear && selectedQuarter === currentQuarter;
      const endQ = `${selectedYear}-${q.end}`;
      return {
        from: `${selectedYear}-${q.start}`,
        to: isCurrQ && todayStr < endQ ? todayStr : endQ,
        periodLabel: `${q.label} ${selectedYear}`,
      };
    }
    if (periodType === 'duration') {
      if (selectedDuration === 'this_month') {
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
        return {
          from: startOfMonth,
          to: todayStr,
          periodLabel: 'Mois en cours',
        };
      }
      if (selectedDuration === 'last_month') {
        const startOfLast = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().slice(0, 10);
        const endOfLast = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().slice(0, 10);
        return {
          from: startOfLast,
          to: endOfLast,
          periodLabel: 'Mois précédent',
        };
      }
      const days = Number(selectedDuration) || 90;
      const start = new Date(now.getTime() - days * 86_400_000).toISOString().slice(0, 10);
      return {
        from: start,
        to: todayStr,
        periodLabel: `${days} derniers jours`,
      };
    }
    // Custom
    return {
      from: customFrom,
      to: customTo,
      periodLabel: `Du ${customFrom} au ${customTo}`,
    };
  }, [periodType, selectedYear, selectedQuarter, selectedDuration, customFrom, customTo, currentYear, currentQuarter, todayStr, now]);

  // Compute comparison (compareFrom, compareTo)
  const { compareFrom, compareTo, compareLabel } = useMemo(() => {
    if (!compareEnabled) {
      return { compareFrom: undefined, compareTo: undefined, compareLabel: '' };
    }

    if (compareMode === 'yoy') {
      // Shift 1 year back
      const fromDt = new Date(from);
      const toDt = new Date(to);
      const cFrom = new Date(fromDt.getFullYear() - 1, fromDt.getMonth(), fromDt.getDate()).toISOString().slice(0, 10);
      const cTo = new Date(toDt.getFullYear() - 1, toDt.getMonth(), toDt.getDate()).toISOString().slice(0, 10);
      return {
        compareFrom: cFrom,
        compareTo: cTo,
        compareLabel: `Même période N-1 (${cFrom} au ${cTo})`,
      };
    }

    // Previous period (consecutive)
    if (periodType === 'quarter') {
      const prevQ = selectedQuarter === 1 ? 4 : selectedQuarter - 1;
      const prevYear = selectedQuarter === 1 ? selectedYear - 1 : selectedYear;
      const q = QUARTER_RANGES[prevQ]!;
      return {
        compareFrom: `${prevYear}-${q.start}`,
        compareTo: `${prevYear}-${q.end}`,
        compareLabel: `Trimestre précédent T${prevQ} ${prevYear}`,
      };
    }

    if (periodType === 'year') {
      return {
        compareFrom: `${selectedYear - 1}-01-01`,
        compareTo: `${selectedYear - 1}-12-31`,
        compareLabel: `Année précédente (${selectedYear - 1})`,
      };
    }

    // Shift back by days difference
    const fromTime = new Date(from).getTime();
    const toTime = new Date(to).getTime();
    const spanMs = Math.max(86_400_000, toTime - fromTime);
    const cTo = new Date(fromTime - 86_400_000).toISOString().slice(0, 10);
    const cFrom = new Date(new Date(cTo).getTime() - spanMs).toISOString().slice(0, 10);
    return {
      compareFrom: cFrom,
      compareTo: cTo,
      compareLabel: `Période précédente (${cFrom} au ${cTo})`,
    };
  }, [compareEnabled, compareMode, from, to, periodType, selectedYear, selectedQuarter]);

  // Live sync diagnostics
  const syncStatusQuery = useQuery({
    queryKey: ['sales-sync-status'],
    queryFn: fetchSalesSyncStatus,
    refetchInterval: 30_000,
  });

  // Trigger manual sync mutation
  const syncMutation = useMutation({
    mutationFn: triggerOrdersSync,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sales-sync-status'] });
      void queryClient.invalidateQueries({ queryKey: ['sales-by-brand'] });
    },
  });

  // Sales by brand query
  const brandQuery = useQuery({
    queryKey: [
      'sales-by-brand',
      from,
      to,
      sort,
      search,
      includeProcessing,
      groupBrands,
      compareEnabled ? compareFrom : null,
      compareEnabled ? compareTo : null,
    ],
    queryFn: () =>
      fetchSalesByBrand({
        from,
        to,
        sort,
        search: search.trim() !== '' ? search.trim() : undefined,
        includeProcessing,
        groupBrands,
        compareFrom: compareEnabled ? compareFrom : undefined,
        compareTo: compareEnabled ? compareTo : undefined,
        limit: 250,
      }),
    refetchInterval: 5 * 60_000,
  });

  const toggleBrandExpand = (brand: string): void => {
    setExpandedBrands((prev) => {
      const next = new Set(prev);
      if (next.has(brand)) next.delete(brand);
      else next.add(brand);
      return next;
    });
  };

  const expandAll = (): void => {
    if (!brandQuery.data?.brands) return;
    setExpandedBrands(new Set(brandQuery.data.brands.map((b) => b.brand)));
  };

  const collapseAll = (): void => {
    setExpandedBrands(new Set());
  };

  const data = brandQuery.data;
  const rawBrands = data?.brands ?? [];
  const totals = data?.totals ?? { units: 0, revenue: 0, orders: 0, brandsCount: 0 };
  const comparisonTotals = data?.comparison ?? null;
  const syncStatus = syncStatusQuery.data;

  // Filter table rows by status (All, Growing, Declining, Grouped)
  const brands = useMemo(() => {
    if (tableFilter === 'growing') {
      return rawBrands.filter((b) => (b.comparison?.revenueGrowthPct ?? 0) > 0);
    }
    if (tableFilter === 'declining') {
      return rawBrands.filter((b) => (b.comparison?.revenueGrowthPct ?? 0) < 0);
    }
    if (tableFilter === 'grouped') {
      return rawBrands.filter((b) => b.isGrouped === true);
    }
    return rawBrands;
  }, [rawBrands, tableFilter]);

  const maxUnits = rawBrands.length > 0 ? Math.max(...rawBrands.map((b) => b.qtySold)) : 1;
  const topBrandByUnits = rawBrands.length > 0 ? [...rawBrands].sort((a, b) => b.qtySold - a.qtySold)[0] : null;
  const topBrandByRev = rawBrands.length > 0 ? [...rawBrands].sort((a, b) => b.revenueTtc - a.revenueTtc)[0] : null;

  // Pareto Top 5 analysis
  const top5Revenue = useMemo(() => {
    const sorted = [...rawBrands].sort((a, b) => b.revenueTtc - a.revenueTtc);
    return sorted.slice(0, 5).reduce((acc, b) => acc + b.revenueTtc, 0);
  }, [rawBrands]);

  const top5SharePct = totals.revenue > 0 ? ((top5Revenue / totals.revenue) * 100).toFixed(1) : '0';

  // Duel Brands selection helper
  const brandAObj = useMemo(() => rawBrands.find((b) => b.brand === duelBrandA) ?? rawBrands[0] ?? null, [rawBrands, duelBrandA]);
  const brandBObj = useMemo(() => rawBrands.find((b) => b.brand === duelBrandB) ?? rawBrands[1] ?? null, [rawBrands, duelBrandB]);

  const openBrandDuel = (defaultA?: string, defaultB?: string): void => {
    if (defaultA) setDuelBrandA(defaultA);
    else if (rawBrands.length > 0 && !duelBrandA) setDuelBrandA(rawBrands[0]!.brand);

    if (defaultB) setDuelBrandB(defaultB);
    else if (rawBrands.length > 1 && !duelBrandB) setDuelBrandB(rawBrands[1]!.brand);

    setDuelOpen(true);
  };

  // CSV Export with Comparison and Sub-Brands
  const exportCsv = (): void => {
    const hasComp = compareEnabled && comparisonTotals !== null;
    const header = [
      'Rang',
      ...(hasComp ? ['Évolution Rang'] : []),
      'Marque',
      'Marque Groupée (Gammes)',
      'Unités Vendues',
      ...(hasComp ? ['Unités Période Préc.', 'Évolution Unités (%)'] : []),
      'CA TTC (TND)',
      ...(hasComp ? ['CA Période Préc. (TND)', 'Évolution CA (%)'] : []),
      'Part de Marché (%)',
      ...(hasComp ? ['Évolution PDM (pts)'] : []),
      'Commandes',
      'Panier Moyen TTC (TND)',
      'Références Vendues',
      'Total Références Catalogue',
      'Stock Total Site',
      'Dernière Vente',
      'Détail des Sous-marques',
    ].join(';');

    const lines = brands.map((b, idx) => {
      const comp = b.comparison;
      const rankMove = comp?.rankDelta !== null && comp?.rankDelta !== undefined
        ? comp.rankDelta > 0 ? `+${comp.rankDelta}` : String(comp.rankDelta)
        : '';
      const subBrandsStr = b.subBrands ? b.subBrands.map((sb) => `${sb.brand} (${sb.qtySold} vendus - ${sb.revenueTtc} TND)`).join(' | ') : '';

      return [
        idx + 1,
        ...(hasComp ? [rankMove] : []),
        `"${b.brand.replace(/"/g, '""')}"`,
        b.isGrouped ? 'Oui' : 'Non',
        b.qtySold,
        ...(hasComp ? [comp?.previousQtySold ?? '', comp?.qtyGrowthPct !== null && comp?.qtyGrowthPct !== undefined ? `${comp.qtyGrowthPct}%` : ''] : []),
        b.revenueTtc,
        ...(hasComp ? [comp?.previousRevenueTtc ?? '', comp?.revenueGrowthPct !== null && comp?.revenueGrowthPct !== undefined ? `${comp.revenueGrowthPct}%` : ''] : []),
        (b.revenueShare * 100).toFixed(2) + '%',
        ...(hasComp ? [comp?.revenueShareDeltaPts !== null && comp?.revenueShareDeltaPts !== undefined ? `${comp.revenueShareDeltaPts > 0 ? '+' : ''}${comp.revenueShareDeltaPts} pt` : ''] : []),
        b.ordersCount,
        b.averageOrderValue,
        b.distinctProductsSold,
        b.catalogProductsCount,
        b.stockQtyTotal ?? '',
        b.lastSoldAt ? b.lastSoldAt.slice(0, 10) : '',
        `"${subBrandsStr.replace(/"/g, '""')}"`,
      ].join(';');
    });

    const blob = new Blob(['\uFEFF' + [header, ...lines].join('\n')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ventes-par-marque-${from}-au-${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5 pb-16">
      {/* Top Header */}
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-amber-100 border border-amber-600/20 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-amber-800">
            S15b · Grossistes B2B
          </span>
          <span className="text-xs text-ink-ghost">·</span>
          <span className="text-xs text-ink-soft">Palmarès & Rotation par Marque</span>
        </div>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-ink font-display">
              Ventes par Marque
            </h1>
            <p className="text-sm text-ink-soft">
              Classement consolidé des marques les plus vendues, parts de marché, panier moyen et rotation depuis les commandes en direct.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => openBrandDuel()}
              disabled={rawBrands.length < 2}
              className="border-herb-600/30 text-herb-800 hover:bg-herb-50"
            >
              <GitCompare className="h-4 w-4 text-herb-700" />
              Duel 2 Marques
            </Button>
            <Link to="/sales-stats">
              <Button variant="outline" size="sm">
                <ShoppingCart className="h-4 w-4" />
                Vue par Produit
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              disabled={brandQuery.isFetching}
              onClick={() => void brandQuery.refetch()}
            >
              <RefreshCw className={cn('h-4 w-4', brandQuery.isFetching && 'animate-spin')} />
              Actualiser
            </Button>
          </div>
        </div>
      </div>

      {/* Sync Health & Diagnostic Banner */}
      <div className="rounded-xl border border-border bg-card p-4 shadow-2xs">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'grid h-10 w-10 place-items-center rounded-lg border',
                syncStatus?.isSyncHealthy
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-600/20'
                  : 'bg-amber-50 text-amber-700 border-amber-600/20',
              )}
            >
              <Zap className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-xs text-ink">
                  Synchronisation Commandes WooCommerce
                </span>
                <span
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full px-2 py-0.2 text-[10px] font-bold uppercase tracking-wider',
                    syncStatus?.isSyncHealthy
                      ? 'bg-emerald-100/80 text-emerald-800 border border-emerald-600/20'
                      : 'bg-amber-100/80 text-amber-800 border border-amber-600/20',
                  )}
                >
                  <span
                    className={cn(
                      'h-1.5 w-1.5 rounded-full',
                      syncStatus?.isSyncHealthy ? 'bg-emerald-600 animate-pulse' : 'bg-amber-600',
                    )}
                  />
                  {syncStatus?.isSyncHealthy ? 'Active (toutes les 15 min)' : 'En attente'}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-ink-soft">
                {syncStatus?.lastOrder ? (
                  <>
                    Dernière commande :{' '}
                    <strong className="text-ink">#{syncStatus.lastOrder.number}</strong> ({timeAgo(syncStatus.lastOrder.dateCreated)}) ·{' '}
                  </>
                ) : null}
                Aujourd’hui :{' '}
                <strong className="text-ink">{syncStatus?.ordersToday.total ?? 0} commandes</strong> intégrées (dont{' '}
                {syncStatus?.ordersToday.byStatus.find((s) => s.status === 'processing')?.count ?? 0} en direct « En cours ») · Total base :{' '}
                {(syncStatus?.totalOrdersInDb ?? 0).toLocaleString('fr-FR')} commandes.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="xs"
            disabled={syncMutation.isPending || syncStatus?.isSyncRunning}
            onClick={() => syncMutation.mutate()}
          >
            <RefreshCw
              className={cn(
                'h-3.5 w-3.5',
                (syncMutation.isPending || syncStatus?.isSyncRunning) && 'animate-spin',
              )}
            />
            {syncMutation.isPending || syncStatus?.isSyncRunning
              ? 'Synchronisation en cours…'
              : 'Synchroniser maintenant'}
          </Button>
        </div>
      </div>

      {/* KPI Cards Ribbon with optional Comparison Badges */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {/* Card 1: CA Global */}
        <div className="rounded-xl border border-border bg-card/90 p-4 shadow-2xs">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-wider text-ink-faint">
              Chiffre d&apos;Affaires Global
            </p>
            {comparisonTotals?.revenueGrowthPct !== null && comparisonTotals?.revenueGrowthPct !== undefined && (
              <span
                className={cn(
                  'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold',
                  comparisonTotals.revenueGrowthPct >= 0
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-rose-100 text-rose-800',
                )}
              >
                {comparisonTotals.revenueGrowthPct >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                {comparisonTotals.revenueGrowthPct >= 0 ? '+' : ''}{comparisonTotals.revenueGrowthPct}%
              </span>
            )}
          </div>
          <p className="mt-1 font-mono text-xl font-bold text-ink">
            {formatTnd(totals.revenue)}
          </p>
          <p className="mt-0.5 text-[11px] text-ink-ghost">
            {compareEnabled && comparisonTotals
              ? `vs ${formatTnd(comparisonTotals.revenue)} (${compareMode === 'yoy' ? 'N-1' : 'préc.'})`
              : 'sur la période sélectionnée'}
          </p>
        </div>

        {/* Card 2: Volume */}
        <div className="rounded-xl border border-border bg-card/90 p-4 shadow-2xs">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-wider text-ink-faint">
              Volume Unités Vendues
            </p>
            {comparisonTotals?.unitsGrowthPct !== null && comparisonTotals?.unitsGrowthPct !== undefined && (
              <span
                className={cn(
                  'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold',
                  comparisonTotals.unitsGrowthPct >= 0
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-rose-100 text-rose-800',
                )}
              >
                {comparisonTotals.unitsGrowthPct >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                {comparisonTotals.unitsGrowthPct >= 0 ? '+' : ''}{comparisonTotals.unitsGrowthPct}%
              </span>
            )}
          </div>
          <p className="mt-1 font-mono text-xl font-bold text-ink">
            {totals.units.toLocaleString('fr-FR')}
          </p>
          <p className="mt-0.5 text-[11px] text-ink-ghost">
            {compareEnabled && comparisonTotals
              ? `vs ${comparisonTotals.units.toLocaleString('fr-FR')} (${compareMode === 'yoy' ? 'N-1' : 'préc.'})`
              : 'articles livrés ou en préparation'}
          </p>
        </div>

        {/* Card 3: Marque #1 Volume */}
        <div className="rounded-xl border border-border bg-card/90 p-4 shadow-2xs">
          <p className="text-[10px] font-bold uppercase tracking-wider text-ink-faint">
            Marque #1 en Volume
          </p>
          <p className="mt-1 font-bold text-base text-ink truncate">
            {topBrandByUnits?.brand ?? '—'}
          </p>
          <p className="mt-0.5 font-mono text-[11px] text-herb-700">
            {topBrandByUnits ? `${topBrandByUnits.qtySold.toLocaleString('fr-FR')} unités` : '—'}
          </p>
        </div>

        {/* Card 4: Marque #1 CA */}
        <div className="rounded-xl border border-border bg-card/90 p-4 shadow-2xs">
          <p className="text-[10px] font-bold uppercase tracking-wider text-ink-faint">
            Marque #1 en Chiffre d&apos;Affaires
          </p>
          <p className="mt-1 font-bold text-base text-ink truncate">
            {topBrandByRev?.brand ?? '—'}
          </p>
          <p className="mt-0.5 font-mono text-[11px] text-herb-700">
            {topBrandByRev ? formatTnd(topBrandByRev.revenueTtc) : '—'}
          </p>
        </div>

        {/* Card 5: Marques Actives */}
        <div className="rounded-xl border border-border bg-card/90 p-4 shadow-2xs">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-wider text-ink-faint">
              Marques Actives
            </p>
            {groupBrands && (
              <span className="inline-flex items-center gap-1 rounded bg-herb-100 text-herb-800 text-[10px] font-bold px-1.5 py-0.2">
                <Layers className="h-3 w-3" />
                Groupées
              </span>
            )}
          </div>
          <p className="mt-1 font-mono text-xl font-bold text-ink">
            {totals.brandsCount.toLocaleString('fr-FR')}
          </p>
          <p className="mt-0.5 text-[11px] text-ink-ghost">avec au moins 1 vente</p>
        </div>
      </div>

      {/* Concentration & Pareto Insight Banner */}
      {rawBrands.length > 0 && totals.revenue > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-herb-600/20 bg-herb-50/60 px-4 py-2.5 text-xs text-herb-950 shadow-2xs">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-herb-700 shrink-0" />
            <span>
              <strong>Concentration des ventes :</strong> Les <strong>5 premières marques</strong> génèrent{' '}
              <strong className="text-herb-800">{top5SharePct}%</strong> du Chiffre d&apos;Affaires global ({formatTnd(top5Revenue)}).
            </span>
          </div>
          <div className="hidden sm:flex items-center gap-2 shrink-0">
            <span className="text-[11px] text-herb-800 font-medium">Part Top 5</span>
            <div className="h-2 w-28 rounded-full bg-herb-200 overflow-hidden">
              <div
                className="h-full bg-herb-600 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(100, Number(top5SharePct))}%` }}
              />
            </div>
            <span className="font-mono text-xs font-bold text-herb-800">{top5SharePct}%</span>
          </div>
        </div>
      )}

      {/* FILTERS & COMPARISON CONTROLS PANEL */}
      <div className="space-y-3 rounded-xl border border-border bg-card/80 p-4 shadow-xs">
        {/* Row 1: Period Selection Modes */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold text-ink uppercase tracking-wider flex items-center gap-1.5 mr-1">
              <Calendar className="h-4 w-4 text-herb-700" />
              Période :
            </span>

            {/* Segmented Mode Picker */}
            <div className="inline-flex rounded-lg border border-border bg-muted/40 p-0.5 text-xs font-semibold">
              <button
                type="button"
                onClick={() => setPeriodType('year')}
                className={cn(
                  'rounded-md px-3 py-1 transition-all',
                  periodType === 'year'
                    ? 'bg-card text-ink shadow-2xs font-bold border border-border/80'
                    : 'text-ink-soft hover:text-ink',
                )}
              >
                Par Année
              </button>
              <button
                type="button"
                onClick={() => setPeriodType('quarter')}
                className={cn(
                  'rounded-md px-3 py-1 transition-all',
                  periodType === 'quarter'
                    ? 'bg-card text-ink shadow-2xs font-bold border border-border/80'
                    : 'text-ink-soft hover:text-ink',
                )}
              >
                Par Trimestre
              </button>
              <button
                type="button"
                onClick={() => setPeriodType('duration')}
                className={cn(
                  'rounded-md px-3 py-1 transition-all',
                  periodType === 'duration'
                    ? 'bg-card text-ink shadow-2xs font-bold border border-border/80'
                    : 'text-ink-soft hover:text-ink',
                )}
              >
                Durées
              </button>
              <button
                type="button"
                onClick={() => setPeriodType('custom')}
                className={cn(
                  'rounded-md px-3 py-1 transition-all',
                  periodType === 'custom'
                    ? 'bg-card text-ink shadow-2xs font-bold border border-border/80'
                    : 'text-ink-soft hover:text-ink',
                )}
              >
                Dates Libres
              </button>
            </div>

            {/* Active Sub-selectors depending on periodType */}
            {periodType === 'year' && (
              <div className="flex items-center gap-1 ml-2">
                {AVAILABLE_YEARS.map((yr) => (
                  <button
                    key={yr}
                    type="button"
                    onClick={() => setSelectedYear(yr)}
                    className={cn(
                      'px-2.5 py-1 rounded-md text-xs font-bold transition-all',
                      selectedYear === yr
                        ? 'bg-herb-600 text-white shadow-2xs'
                        : 'bg-muted/60 text-ink-soft hover:bg-muted hover:text-ink border border-border/60',
                    )}
                  >
                    {yr === currentYear ? (
                      <span className="flex items-center gap-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-300 animate-pulse" />
                        {yr} (En cours)
                      </span>
                    ) : (
                      yr
                    )}
                  </button>
                ))}
              </div>
            )}

            {periodType === 'quarter' && (
              <div className="flex items-center gap-2 ml-2">
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="h-8 rounded-md border border-border bg-card px-2 text-xs font-bold text-ink shadow-2xs"
                >
                  {AVAILABLE_YEARS.map((yr) => (
                    <option key={yr} value={yr}>
                      {yr}
                    </option>
                  ))}
                </select>

                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4].map((qNum) => {
                    const isCurrent = selectedYear === currentYear && currentQuarter === qNum;
                    return (
                      <button
                        key={qNum}
                        type="button"
                        onClick={() => setSelectedQuarter(qNum)}
                        className={cn(
                          'px-2.5 py-1 rounded-md text-xs font-bold transition-all',
                          selectedQuarter === qNum
                            ? 'bg-herb-600 text-white shadow-2xs'
                            : 'bg-muted/60 text-ink-soft hover:bg-muted hover:text-ink border border-border/60',
                        )}
                      >
                        {isCurrent ? `T${qNum} (Actuel)` : `T${qNum}`}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {periodType === 'duration' && (
              <div className="flex items-center gap-1.5 ml-2">
                {[
                  { value: '30', label: '30 jours' },
                  { value: '90', label: '90 jours' },
                  { value: '180', label: '180 jours' },
                  { value: '365', label: '12 mois' },
                  { value: 'this_month', label: 'Ce mois' },
                  { value: 'last_month', label: 'Mois dernier' },
                ].map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => setSelectedDuration(item.value)}
                    className={cn(
                      'px-2.5 py-1 rounded-md text-xs font-semibold transition-all',
                      selectedDuration === item.value
                        ? 'bg-herb-600 text-white font-bold shadow-2xs'
                        : 'bg-muted/60 text-ink-soft hover:bg-muted hover:text-ink border border-border/60',
                    )}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}

            {periodType === 'custom' && (
              <div className="flex items-center gap-2 ml-2">
                <input
                  type="date"
                  value={customFrom}
                  min="2023-03-01"
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="h-8 rounded-md border border-border bg-card px-2 text-xs font-semibold text-ink shadow-2xs"
                />
                <span className="text-xs text-ink-soft font-medium">au</span>
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="h-8 rounded-md border border-border bg-card px-2 text-xs font-semibold text-ink shadow-2xs"
                />
              </div>
            )}
          </div>

          {/* Active Period Feedback Chip */}
          <div className="flex items-center gap-2">
            <span className="rounded-lg bg-herb-100/80 border border-herb-600/20 px-2.5 py-1 text-xs font-bold text-herb-900">
              {periodLabel}
            </span>
          </div>
        </div>

        {/* Row 2: Comparison & Grouping Switches */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-3">
          {/* Comparison Toggle & Mode */}
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setCompareEnabled((prev) => !prev)}
              className={cn(
                'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border',
                compareEnabled
                  ? 'bg-emerald-600 text-white border-emerald-700 shadow-2xs'
                  : 'bg-card text-ink-soft hover:bg-muted hover:text-ink border-border',
              )}
            >
              <GitCompare className="h-4 w-4" />
              <span>{compareEnabled ? 'Comparaison active' : 'Activer comparaison'}</span>
            </button>

            {compareEnabled && (
              <div className="flex items-center gap-2">
                <select
                  value={compareMode}
                  onChange={(e) => setCompareMode(e.target.value as CompareMode)}
                  className="h-8 rounded-md border border-emerald-600/30 bg-emerald-50/50 px-2.5 text-xs font-bold text-emerald-950 shadow-2xs"
                >
                  <option value="yoy">Même période N-1 (Année précédente)</option>
                  <option value="previous_period">Période précédente consécutive</option>
                </select>

                <span className="text-xs text-ink-soft italic">
                  ({compareLabel})
                </span>
              </div>
            )}
          </div>

          {/* Brand Grouping Switch (SVR, Sun Secure, Sebiaclear...) */}
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs font-bold text-ink cursor-pointer select-none bg-muted/40 hover:bg-muted px-2.5 py-1.5 rounded-lg border border-border/80 transition-colors">
              <input
                type="checkbox"
                checked={groupBrands}
                onChange={(e) => setGroupBrands(e.target.checked)}
                className="h-4 w-4 rounded border-border text-herb-600 focus:ring-herb-500"
              />
              <span className="flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5 text-herb-700" />
                Regrouper les gammes (SVR = SVR + Sebiaclear + Sun Secure)
              </span>
            </label>
          </div>
        </div>

        {/* Row 3: Sorting, Search, Live Processing & Actions */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
          <div className="flex flex-wrap items-center gap-3">
            {/* Sort */}
            <label className="flex items-center gap-1.5 text-xs font-semibold text-ink">
              <span className="text-ink-faint uppercase text-[10px] tracking-wider">Trier par :</span>
              <select
                value={sort}
                onChange={(e) =>
                  setSort(e.target.value as 'qty' | 'revenue' | 'orders' | 'products')
                }
                className="h-8 rounded-lg border border-border bg-card px-2.5 text-xs font-semibold text-ink shadow-2xs"
              >
                <option value="revenue">Chiffre d&apos;affaires TTC (Valeur)</option>
                <option value="qty">Unités vendues (Volume)</option>
                <option value="orders">Nombre de commandes</option>
                <option value="products">Nombre de références vendues</option>
              </select>
            </label>

            {/* Search */}
            <div className="relative">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Chercher marque ou gamme (ex: SVR, Avene, Sebiaclear)…"
                className="h-8 min-w-64 rounded-lg border border-border bg-card pl-8 pr-3 text-xs font-semibold text-ink shadow-2xs placeholder:text-ink-ghost focus:border-herb-600 focus:outline-none"
              />
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-ink-ghost" />
            </div>

            {/* Live orders check */}
            <label className="flex items-center gap-1.5 text-xs font-medium text-ink cursor-pointer select-none">
              <input
                type="checkbox"
                checked={includeProcessing}
                onChange={(e) => setIncludeProcessing(e.target.checked)}
                className="h-4 w-4 rounded border-border text-herb-600 focus:ring-herb-500"
              />
              <span className="text-xs text-ink-soft">Commandes du jour (« En cours »)</span>
            </label>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="xs"
              onClick={expandedBrands.size > 0 ? collapseAll : expandAll}
            >
              {expandedBrands.size > 0 ? 'Tout replier' : 'Tout déplier'}
            </Button>
            <Button
              variant="outline"
              size="xs"
              disabled={brands.length === 0}
              onClick={exportCsv}
            >
              Exporter CSV
            </Button>
          </div>
        </div>
      </div>

      {/* Table Filter Tabs (Toutes, Croissance, Baisse, Groupées) */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setTableFilter('all')}
            className={cn(
              'px-3 py-1.5 rounded-lg text-xs font-bold transition-all border',
              tableFilter === 'all'
                ? 'bg-ink text-white border-ink shadow-2xs'
                : 'bg-card text-ink-soft hover:bg-muted hover:text-ink border-border',
            )}
          >
            Toutes les marques ({rawBrands.length})
          </button>
          {compareEnabled && (
            <>
              <button
                type="button"
                onClick={() => setTableFilter('growing')}
                className={cn(
                  'px-3 py-1.5 rounded-lg text-xs font-bold transition-all border flex items-center gap-1',
                  tableFilter === 'growing'
                    ? 'bg-emerald-600 text-white border-emerald-700 shadow-2xs'
                    : 'bg-card text-emerald-800 hover:bg-emerald-50 border-emerald-600/30',
                )}
              >
                <ArrowUpRight className="h-3.5 w-3.5" />
                En croissance ({rawBrands.filter((b) => (b.comparison?.revenueGrowthPct ?? 0) > 0).length})
              </button>
              <button
                type="button"
                onClick={() => setTableFilter('declining')}
                className={cn(
                  'px-3 py-1.5 rounded-lg text-xs font-bold transition-all border flex items-center gap-1',
                  tableFilter === 'declining'
                    ? 'bg-rose-600 text-white border-rose-700 shadow-2xs'
                    : 'bg-card text-rose-800 hover:bg-rose-50 border-rose-600/30',
                )}
              >
                <ArrowDownRight className="h-3.5 w-3.5" />
                En recul ({rawBrands.filter((b) => (b.comparison?.revenueGrowthPct ?? 0) < 0).length})
              </button>
            </>
          )}
          {groupBrands && (
            <button
              type="button"
              onClick={() => setTableFilter('grouped')}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-bold transition-all border flex items-center gap-1',
                tableFilter === 'grouped'
                  ? 'bg-herb-700 text-white border-herb-800 shadow-2xs'
                  : 'bg-card text-herb-800 hover:bg-herb-50 border-herb-600/30',
              )}
            >
              <Layers className="h-3.5 w-3.5" />
              Marques groupées ({rawBrands.filter((b) => b.isGrouped).length})
            </button>
          )}
        </div>

        <span className="text-xs text-ink-ghost">
          {brands.length} marque(s) affichée(s)
        </span>
      </div>

      {/* Leaderboard Table */}
      <div className="rounded-xl border border-border bg-card shadow-xs overflow-hidden">
        {brandQuery.isLoading ? (
          <div className="flex items-center justify-center p-12 text-xs text-ink-soft">
            <RefreshCw className="mr-2 h-4 w-4 animate-spin text-herb-600" />
            Agrégation et calcul des ventes par marque en cours…
          </div>
        ) : brands.length === 0 ? (
          <div className="p-12 text-center text-xs text-ink-soft">
            Aucune vente trouvée pour les critères et filtres sélectionnés.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-border bg-muted/60 text-[10px] font-bold uppercase tracking-wider text-ink-soft">
                <tr>
                  <th className="px-4 py-3 text-center w-14">#</th>
                  <th className="px-4 py-3">Marque</th>
                  <th className="px-4 py-3 text-right">Unités Vendues</th>
                  <th className="px-4 py-3 text-right">CA TTC</th>
                  <th className="px-4 py-3 text-right">Part de Marché</th>
                  <th className="px-4 py-3 text-right">Commandes</th>
                  <th className="px-4 py-3 text-right">Panier Moyen</th>
                  <th className="px-4 py-3 text-right">Réf. Vendues / Total</th>
                  <th className="px-4 py-3 text-right">Stock Rayon</th>
                  <th className="px-4 py-3 text-center w-10">Détails</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {brands.map((b, idx) => {
                  const isExpanded = expandedBrands.has(b.brand);
                  const rank = idx + 1;
                  const unitPercent = Math.min(100, Math.round((b.qtySold / maxUnits) * 100));

                  return (
                    <BrandRow
                      key={b.brand}
                      row={b}
                      rank={rank}
                      unitPercent={unitPercent}
                      isExpanded={isExpanded}
                      hasComparison={compareEnabled && comparisonTotals !== null}
                      onToggle={() => toggleBrandExpand(b.brand)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* BRAND DUEL MODAL (Head-to-Head Comparison) */}
      {duelOpen && brandAObj && brandBObj && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs">
          <div className="w-full max-w-3xl rounded-2xl border border-border bg-card p-6 shadow-2xl space-y-5 animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div className="flex items-center gap-2">
                <div className="grid h-8 w-8 place-items-center rounded-lg bg-herb-100 text-herb-800">
                  <GitCompare className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-bold text-base text-ink">Duel de Marques : Face-à-Face</h3>
                  <p className="text-xs text-ink-soft">Benchmark comparatif direct sur la période ({periodLabel})</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDuelOpen(false)}
                className="grid h-8 w-8 place-items-center rounded-lg hover:bg-muted text-ink-ghost hover:text-ink transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Brand Selectors */}
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl border border-herb-600/30 bg-herb-50/40 p-3 space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-wider text-herb-800 block">
                  Marque A
                </label>
                <select
                  value={brandAObj.brand}
                  onChange={(e) => setDuelBrandA(e.target.value)}
                  className="w-full h-9 rounded-lg border border-border bg-card px-2.5 text-xs font-bold text-ink shadow-xs"
                >
                  {rawBrands.map((b) => (
                    <option key={b.brand} value={b.brand}>
                      {b.brand} ({formatTnd(b.revenueTtc)})
                    </option>
                  ))}
                </select>
              </div>

              <div className="rounded-xl border border-sky-600/30 bg-sky-50/40 p-3 space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-wider text-sky-800 block">
                  Marque B
                </label>
                <select
                  value={brandBObj.brand}
                  onChange={(e) => setDuelBrandB(e.target.value)}
                  className="w-full h-9 rounded-lg border border-border bg-card px-2.5 text-xs font-bold text-ink shadow-xs"
                >
                  {rawBrands.map((b) => (
                    <option key={b.brand} value={b.brand}>
                      {b.brand} ({formatTnd(b.revenueTtc)})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Visual Head-to-Head Ratio Bar */}
            <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-2">
              <div className="flex items-center justify-between text-xs font-bold">
                <span className="text-herb-800 flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-herb-600" />
                  {brandAObj.brand} ({(brandAObj.revenueTtc / Math.max(1, brandAObj.revenueTtc + brandBObj.revenueTtc) * 100).toFixed(1)}%)
                </span>
                <span className="text-ink-soft uppercase text-[10px] tracking-wider">Répartition CA</span>
                <span className="text-sky-800 flex items-center gap-1.5">
                  {(brandBObj.revenueTtc / Math.max(1, brandAObj.revenueTtc + brandBObj.revenueTtc) * 100).toFixed(1)}% {brandBObj.brand}
                  <span className="h-2 w-2 rounded-full bg-sky-600" />
                </span>
              </div>
              <div className="h-3 w-full rounded-full bg-muted overflow-hidden flex">
                <div
                  className="h-full bg-herb-600 transition-all duration-500"
                  style={{
                    width: `${(brandAObj.revenueTtc / Math.max(1, brandAObj.revenueTtc + brandBObj.revenueTtc)) * 100}%`,
                  }}
                />
                <div
                  className="h-full bg-sky-600 transition-all duration-500"
                  style={{
                    width: `${(brandBObj.revenueTtc / Math.max(1, brandAObj.revenueTtc + brandBObj.revenueTtc)) * 100}%`,
                  }}
                />
              </div>
            </div>

            {/* Comparison Metrics Grid */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              {/* CA TTC */}
              <div className="rounded-xl border border-border bg-card p-3 flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-ink-ghost">Chiffre d&apos;Affaires</p>
                  <p className="font-mono font-bold text-sm text-herb-700">{formatTnd(brandAObj.revenueTtc)}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-ink-ghost">Chiffre d&apos;Affaires</p>
                  <p className="font-mono font-bold text-sm text-sky-700">{formatTnd(brandBObj.revenueTtc)}</p>
                </div>
              </div>

              {/* Unités */}
              <div className="rounded-xl border border-border bg-card p-3 flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-ink-ghost">Unités Vendues</p>
                  <p className="font-mono font-bold text-sm text-herb-700">{brandAObj.qtySold.toLocaleString('fr-FR')} un.</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-ink-ghost">Unités Vendues</p>
                  <p className="font-mono font-bold text-sm text-sky-700">{brandBObj.qtySold.toLocaleString('fr-FR')} un.</p>
                </div>
              </div>

              {/* Part de marché */}
              <div className="rounded-xl border border-border bg-card p-3 flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-ink-ghost">Part de Marché</p>
                  <p className="font-mono font-bold text-sm text-herb-700">{(brandAObj.revenueShare * 100).toFixed(2)}%</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-ink-ghost">Part de Marché</p>
                  <p className="font-mono font-bold text-sm text-sky-700">{(brandBObj.revenueShare * 100).toFixed(2)}%</p>
                </div>
              </div>

              {/* Panier Moyen */}
              <div className="rounded-xl border border-border bg-card p-3 flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-ink-ghost">Panier Moyen</p>
                  <p className="font-mono font-bold text-sm text-herb-700">{formatTnd(brandAObj.averageOrderValue)}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-ink-ghost">Panier Moyen</p>
                  <p className="font-mono font-bold text-sm text-sky-700">{formatTnd(brandBObj.averageOrderValue)}</p>
                </div>
              </div>
            </div>

            {/* Top Products Comparison */}
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div>
                <p className="text-xs font-bold text-herb-900 mb-2">Top 3 Produits — {brandAObj.brand}</p>
                <div className="space-y-1.5">
                  {brandAObj.topProducts.slice(0, 3).map((p, i) => (
                    <div key={p.sku} className="rounded-lg border border-border bg-card p-2 text-xs flex items-center justify-between">
                      <div className="min-w-0 flex-1 truncate pr-2">
                        <span className="font-mono font-bold text-ink-soft mr-1">#{i + 1}</span>
                        <span className="font-medium text-ink">{p.name}</span>
                      </div>
                      <span className="font-mono font-bold text-herb-700 shrink-0">{p.qtySold} un.</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-bold text-sky-900 mb-2">Top 3 Produits — {brandBObj.brand}</p>
                <div className="space-y-1.5">
                  {brandBObj.topProducts.slice(0, 3).map((p, i) => (
                    <div key={p.sku} className="rounded-lg border border-border bg-card p-2 text-xs flex items-center justify-between">
                      <div className="min-w-0 flex-1 truncate pr-2">
                        <span className="font-mono font-bold text-ink-soft mr-1">#{i + 1}</span>
                        <span className="font-medium text-ink">{p.name}</span>
                      </div>
                      <span className="font-mono font-bold text-sky-700 shrink-0">{p.qtySold} un.</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button variant="outline" size="sm" onClick={() => setDuelOpen(false)}>
                Fermer le duel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BrandRow({
  row,
  rank,
  unitPercent,
  isExpanded,
  hasComparison,
  onToggle,
}: {
  readonly row: BrandSalesRow;
  readonly rank: number;
  readonly unitPercent: number;
  readonly isExpanded: boolean;
  readonly hasComparison: boolean;
  readonly onToggle: () => void;
}): React.ReactElement {
  const comp = row.comparison;

  return (
    <>
      <tr
        onClick={onToggle}
        className={cn(
          'cursor-pointer transition-colors hover:bg-muted/40 select-none',
          isExpanded && 'bg-muted/30 font-medium',
        )}
      >
        {/* Rank badge + Movement */}
        <td className="px-4 py-3 text-center">
          <div className="flex flex-col items-center justify-center gap-1">
            {rank === 1 ? (
              <span className="grid h-6 w-6 place-items-center rounded-full bg-amber-400 font-bold text-amber-950 text-xs shadow-2xs mx-auto">
                1
              </span>
            ) : rank === 2 ? (
              <span className="grid h-6 w-6 place-items-center rounded-full bg-slate-300 font-bold text-slate-800 text-xs shadow-2xs mx-auto">
                2
              </span>
            ) : rank === 3 ? (
              <span className="grid h-6 w-6 place-items-center rounded-full bg-amber-600 font-bold text-white text-xs shadow-2xs mx-auto">
                3
              </span>
            ) : (
              <span className="font-mono text-ink-ghost font-medium text-xs">
                {rank}
              </span>
            )}

            {/* Rank Movement Indicator */}
            {hasComparison && comp && (
              <div className="flex items-center text-[10px] font-bold">
                {comp.rankDelta !== null && comp.rankDelta > 0 ? (
                  <span className="text-emerald-700 flex items-center">
                    ▲+{comp.rankDelta}
                  </span>
                ) : comp.rankDelta !== null && comp.rankDelta < 0 ? (
                  <span className="text-rose-700 flex items-center">
                    ▼{comp.rankDelta}
                  </span>
                ) : (
                  <span className="text-ink-ghost font-mono">=</span>
                )}
              </div>
            )}
          </div>
        </td>

        {/* Brand name, Grouping Badge & Catalogue link */}
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'grid h-7 w-7 shrink-0 place-items-center rounded-lg border text-xs font-bold',
                row.isGrouped
                  ? 'bg-amber-100/80 border-amber-600/30 text-amber-900'
                  : 'bg-herb-100/70 border-herb-600/20 text-herb-900',
              )}
              aria-hidden="true"
            >
              {row.brand.slice(0, 2).toUpperCase()}
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <p className="font-bold text-ink truncate text-sm">{row.brand}</p>
                {row.isGrouped && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-amber-100/90 text-amber-900 border border-amber-600/20 px-2 py-0.2 text-[10px] font-bold"
                    title={row.subBrands ? `Gammes incluses : ${row.subBrands.map((s) => s.brand).join(', ')}` : 'Marque consolidée'}
                  >
                    <Layers className="h-2.5 w-2.5 text-amber-700" />
                    {row.subBrands ? `${row.subBrands.length} gammes` : 'Groupée'}
                  </span>
                )}
              </div>
              <Link
                to={`/products?brand=${encodeURIComponent(row.brand)}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-[11px] text-herb-700 hover:underline"
              >
                Voir au catalogue
                <ExternalLink className="h-2.5 w-2.5" />
              </Link>
            </div>
          </div>
        </td>

        {/* Units Sold + Mini progress bar + Comparison Delta */}
        <td className="px-4 py-3 text-right">
          <div className="flex items-baseline justify-end gap-1.5">
            <p className="font-mono font-bold text-ink text-sm">
              {row.qtySold.toLocaleString('fr-FR')}
            </p>
            {hasComparison && comp?.qtyGrowthPct !== null && comp?.qtyGrowthPct !== undefined && (
              <span
                className={cn(
                  'inline-flex items-center text-[10px] font-bold',
                  comp.qtyGrowthPct >= 0 ? 'text-emerald-700' : 'text-rose-700',
                )}
              >
                {comp.qtyGrowthPct >= 0 ? '+' : ''}{comp.qtyGrowthPct}%
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center justify-end gap-1.5">
            <div className="h-1.5 w-20 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-herb-600 rounded-full"
                style={{ width: `${unitPercent}%` }}
              />
            </div>
            <span className="text-[10px] text-ink-ghost font-mono">{unitPercent}%</span>
          </div>
        </td>

        {/* Revenue TTC + Comparison Growth */}
        <td className="px-4 py-3 text-right font-mono">
          <p className="font-bold text-ink text-sm">
            {formatTnd(row.revenueTtc)}
          </p>
          {hasComparison && comp?.revenueGrowthPct !== null && comp?.revenueGrowthPct !== undefined && (
            <p
              className={cn(
                'text-[10px] font-bold mt-0.5',
                comp.revenueGrowthPct >= 0 ? 'text-emerald-700' : 'text-rose-700',
              )}
            >
              {comp.revenueGrowthPct >= 0 ? '▲ +' : '▼ '}{comp.revenueGrowthPct}% vs comp.
            </p>
          )}
        </td>

        {/* Market Share % */}
        <td className="px-4 py-3 text-right">
          <div className="flex flex-col items-end gap-0.5">
            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100/70 text-emerald-800 border border-emerald-600/20">
              {(row.revenueShare * 100).toFixed(1)}%
            </span>
            {hasComparison && comp?.revenueShareDeltaPts !== null && comp?.revenueShareDeltaPts !== undefined && (
              <span
                className={cn(
                  'text-[10px] font-bold font-mono',
                  comp.revenueShareDeltaPts >= 0 ? 'text-emerald-700' : 'text-rose-700',
                )}
              >
                ({comp.revenueShareDeltaPts >= 0 ? '+' : ''}{comp.revenueShareDeltaPts} pt)
              </span>
            )}
          </div>
        </td>

        {/* Orders Count */}
        <td className="px-4 py-3 text-right font-mono text-ink">
          {row.ordersCount.toLocaleString('fr-FR')}
        </td>

        {/* Average Order Value */}
        <td className="px-4 py-3 text-right font-mono text-ink-soft">
          {formatTnd(row.averageOrderValue)}
        </td>

        {/* Active references vs Catalog Total */}
        <td className="px-4 py-3 text-right font-mono text-ink">
          <span>{row.distinctProductsSold}</span>
          <span className="text-ink-ghost"> / {row.catalogProductsCount}</span>
        </td>

        {/* Stock */}
        <td className="px-4 py-3 text-right font-mono">
          {row.stockQtyTotal !== null ? (
            <span
              className={cn(
                'font-semibold',
                row.stockQtyTotal > 0 ? 'text-emerald-700' : 'text-rose-700',
              )}
            >
              {row.stockQtyTotal.toLocaleString('fr-FR')}
            </span>
          ) : (
            <span className="text-ink-ghost">—</span>
          )}
        </td>

        {/* Expand caret */}
        <td className="px-4 py-3 text-center text-ink-ghost">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 mx-auto" />
          ) : (
            <ChevronRight className="h-4 w-4 mx-auto" />
          )}
        </td>
      </tr>

      {/* Collapsible Accordion: Sub-Brands Breakdown + Top 5 Best-Selling Products */}
      {isExpanded && (
        <tr className="bg-muted/20">
          <td colSpan={10} className="px-6 py-4 space-y-4">
            {/* SUB-BRANDS BREAKDOWN (When grouped) */}
            {row.subBrands && row.subBrands.length > 1 && (
              <div className="rounded-xl border border-amber-600/30 bg-amber-50/40 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Layers className="h-4 w-4 text-amber-800" />
                    <p className="text-xs font-bold uppercase tracking-wider text-amber-950">
                      Consolidation des Gammes & Sous-Marques ({row.subBrands.length})
                    </p>
                  </div>
                  <span className="text-[11px] text-amber-800 font-medium">
                    Total combiné : {formatTnd(row.revenueTtc)}
                  </span>
                </div>

                {/* Sub-brand visual bar */}
                <div className="h-2.5 w-full rounded-full bg-amber-200/60 overflow-hidden flex">
                  {row.subBrands.map((sb, sbIdx) => {
                    const colors = ['bg-amber-600', 'bg-emerald-600', 'bg-sky-600', 'bg-purple-600', 'bg-rose-600'];
                    const color = colors[sbIdx % colors.length]!;
                    return (
                      <div
                        key={sb.brand}
                        className={cn('h-full', color)}
                        style={{ width: `${sb.shareOfBrandRevenue * 100}%` }}
                        title={`${sb.brand} : ${(sb.shareOfBrandRevenue * 100).toFixed(1)}%`}
                      />
                    );
                  })}
                </div>

                {/* Sub-brands cards grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 pt-1">
                  {row.subBrands.map((sb, sbIdx) => {
                    const colors = ['border-amber-600/30 bg-card', 'border-emerald-600/30 bg-card', 'border-sky-600/30 bg-card', 'border-purple-600/30 bg-card'];
                    const cardColor = colors[sbIdx % colors.length]!;
                    return (
                      <div
                        key={sb.brand}
                        className={cn('rounded-lg border p-2.5 shadow-2xs space-y-1', cardColor)}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-xs text-ink truncate">{sb.brand}</span>
                          <span className="font-mono text-[10px] font-bold text-herb-800">
                            {(sb.shareOfBrandRevenue * 100).toFixed(1)}%
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-[11px] text-ink-soft">
                          <span>{sb.qtySold} vendus</span>
                          <span className="font-mono font-bold text-ink">{formatTnd(sb.revenueTtc)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* TOP 5 BEST-SELLING PRODUCTS */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-ink">
                  Top Produits Phares de {row.brand}
                </p>
                <span className="text-[11px] text-ink-ghost">
                  {row.topProducts.length} référence(s) affichée(s)
                </span>
              </div>
              {row.topProducts.length === 0 ? (
                <p className="text-xs text-ink-ghost italic">
                  Aucun détail produit disponible pour cette marque.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {row.topProducts.map((p, pIdx) => (
                    <div
                      key={p.sku}
                      className="flex items-center gap-3 rounded-lg border border-border bg-card p-3 shadow-2xs"
                    >
                      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-muted text-[10px] font-bold text-ink-soft">
                        {pIdx + 1}
                      </span>
                      {p.image ? (
                        <img
                          src={p.image}
                          alt=""
                          className="h-10 w-10 shrink-0 rounded-md object-cover border border-border/80"
                        />
                      ) : (
                        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-muted text-ink-ghost border border-border/60">
                          <Package className="h-5 w-5" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-xs text-ink truncate" title={p.name}>
                          {p.name}
                        </p>
                        <p className="font-mono text-[10px] text-ink-ghost">{p.sku}</p>
                        <div className="mt-1 flex items-center justify-between text-xs">
                          <span className="font-mono font-bold text-herb-700">
                            {p.qtySold} vendus
                          </span>
                          <span className="font-mono text-ink-soft font-medium">
                            {formatTnd(p.revenueTtc)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
